import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ProgressPanel from "./ProgressPanel";
import SpaceWorld from "./SpaceWorld";
import {
  clamp,
  getJourneyCamera,
  getJourneyPercent,
  getJourneyStats,
} from "./journeyModel";
import { useBigCanadaRunProgressData } from "./progressData";
import { SendAnalyticsEvent } from "../../func/analyticsFunctions";
import "./spaceDemo.css";

const DEFAULT_SCROLL_PROGRESS = 0.5;
const SCROLL_CAMERA_RANGE = 3000;
const DEFAULT_VIEW_ZOOM = 1;
const VIEW_ZOOM_MIN = 0.3;
const VIEW_ZOOM_MAX = 2.2;
const VIEW_ZOOM_STEP = 0.01;

function getTouchDistance(touches) {
  const firstTouch = touches[0];
  const secondTouch = touches[1];
  const dx = firstTouch.clientX - secondTouch.clientX;
  const dy = firstTouch.clientY - secondTouch.clientY;

  return Math.hypot(dx, dy);
}

function getElementDocumentTop(element) {
  return element.getBoundingClientRect().top + window.scrollY;
}

function snapToDevicePixel(value) {
  if (typeof window === "undefined") return value;

  const pixelRatio = window.devicePixelRatio || 1;
  return Math.round(value * pixelRatio) / pixelRatio;
}

export default function BigCanadaRunSpaceDemo() {
  const sectionRef = useRef(null);
  const stickyRef = useRef(null);
  const isJumpingRef = useRef(false);
  const panRef = useRef(null);
  const pinchRef = useRef(null);
  const viewZoomRef = useRef(DEFAULT_VIEW_ZOOM);
  const [isPanning, setIsPanning] = useState(false);
  const [panelCollapsed, setPanelCollapsed] = useState(true);
  const [scrollProgress, setScrollProgress] = useState(DEFAULT_SCROLL_PROGRESS);
  const [viewPan, setViewPan] = useState({ x: 0, y: 0 });
  const [viewZoom, setViewZoom] = useState(DEFAULT_VIEW_ZOOM);
  const [viewport, setViewport] = useState({ width: 1200, height: 800 });
  const [calibrationPoints, setCalibrationPoints] = useState([]);
  const [calibrationPlacementEnabled, setCalibrationPlacementEnabled] = useState(false);

  const progressDebugEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;

    return new URLSearchParams(window.location.search).get("progressDebug") === "1";
  }, []);
  const { progressData, setDistanceKm } = useBigCanadaRunProgressData({
    debugEnabled: progressDebugEnabled,
  });
  const { distanceKm } = progressData;
  const calibrationEnabled = useMemo(() => {
    if (typeof window === "undefined") return false;

    return new URLSearchParams(window.location.search).get("routeDebug") === "1";
  }, []);
  const camera = useMemo(() => getJourneyCamera(distanceKm), [distanceKm]);
  const stats = useMemo(() => getJourneyStats(distanceKm), [distanceKm]);
  const journeyPercent = getJourneyPercent(distanceKm);

  const setClampedZoom = useCallback((nextZoom) => {
    setViewZoom(clamp(nextZoom, VIEW_ZOOM_MIN, VIEW_ZOOM_MAX));
  }, []);

  const updateFromScroll = useCallback(() => {
    const section = sectionRef.current;
    if (!section || isJumpingRef.current) return;

    const scrollable = section.offsetHeight - window.innerHeight;
    if (scrollable <= 0) return;

    const progress = clamp((window.scrollY - getElementDocumentTop(section)) / scrollable, 0, 1);
    setScrollProgress(progress);
  }, []);

  const jumpToScrollProgress = useCallback((nextProgress, behavior = "auto") => {
    const section = sectionRef.current;
    if (!section) return;

    const scrollable = section.offsetHeight - window.innerHeight;
    const progress = clamp(nextProgress, 0, 1);
    const top = getElementDocumentTop(section) + scrollable * progress;

    isJumpingRef.current = true;
    setScrollProgress(progress);
    window.scrollTo({ top, behavior });
    window.setTimeout(() => {
      isJumpingRef.current = false;
    }, behavior === "smooth" ? 450 : 80);
  }, []);

  const changeViewZoom = useCallback(
    (nextZoom) => {
      setClampedZoom(nextZoom);
    },
    [setClampedZoom]
  );

  const togglePanel = useCallback(() => {
    setPanelCollapsed((currentValue) => {
      if (currentValue) {
        SendAnalyticsEvent("tbcr_moon_challenge_card_expanded", {
          distance_km: Math.round(distanceKm),
          lap: stats.currentLap,
          phase: stats.phase,
        });
      }

      return !currentValue;
    });
  }, [distanceKm, stats.currentLap, stats.phase]);

  const trackCtaClick = useCallback(() => {
    SendAnalyticsEvent("tbcr_moon_challenge_cta_click", {
      link_url: "https://thebigcanadarun.ca/",
      distance_km: Math.round(distanceKm),
      lap: stats.currentLap,
      phase: stats.phase,
    });
  }, [distanceKm, stats.currentLap, stats.phase]);

  const preventControlFocusScroll = useCallback((event) => {
    event.preventDefault();
  }, []);

  const beginPan = useCallback(
    (event) => {
      if (event.target.closest(".bcr-demo-panel")) return;
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      event.currentTarget.setPointerCapture?.(event.pointerId);
      panRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        panX: viewPan.x,
        panY: viewPan.y,
      };
      setIsPanning(true);
    },
    [viewPan.x, viewPan.y]
  );

  const movePan = useCallback((event) => {
    if (!panRef.current || panRef.current.pointerId !== event.pointerId) return;

    event.preventDefault();
    setViewPan({
      x: panRef.current.panX + event.clientX - panRef.current.startX,
      y: panRef.current.panY + event.clientY - panRef.current.startY,
    });
  }, []);

  const endPan = useCallback((event) => {
    if (!panRef.current || panRef.current.pointerId !== event.pointerId) return;

    event.currentTarget.releasePointerCapture?.(event.pointerId);
    panRef.current = null;
    setIsPanning(false);
  }, []);

  const addCalibrationPoint = useCallback((point) => {
    setCalibrationPoints((currentPoints) => [
      ...currentPoints,
      {
        id: currentPoints.length + 1,
        ...point,
      },
    ]);
  }, []);

  useEffect(() => {
    viewZoomRef.current = viewZoom;
  }, [viewZoom]);

  useEffect(() => {
    const stickyEl = stickyRef.current;
    if (!stickyEl) return undefined;

    const handleTouchStart = (event) => {
      if (event.touches.length !== 2) return;

      pinchRef.current = {
        distance: getTouchDistance(event.touches),
        zoom: viewZoomRef.current,
      };
    };

    const handleTouchMove = (event) => {
      if (event.touches.length !== 2 || !pinchRef.current) return;

      event.preventDefault();
      const nextDistance = getTouchDistance(event.touches);
      const nextZoom = pinchRef.current.zoom * (nextDistance / pinchRef.current.distance);
      setClampedZoom(nextZoom);
    };

    const handleTouchEnd = (event) => {
      if (event.touches.length < 2) {
        pinchRef.current = null;
      }
    };

    stickyEl.addEventListener("touchstart", handleTouchStart, { passive: true });
    stickyEl.addEventListener("touchmove", handleTouchMove, { passive: false });
    stickyEl.addEventListener("touchend", handleTouchEnd);
    stickyEl.addEventListener("touchcancel", handleTouchEnd);

    return () => {
      stickyEl.removeEventListener("touchstart", handleTouchStart);
      stickyEl.removeEventListener("touchmove", handleTouchMove);
      stickyEl.removeEventListener("touchend", handleTouchEnd);
      stickyEl.removeEventListener("touchcancel", handleTouchEnd);
    };
  }, [setClampedZoom]);

  useEffect(() => {
    const handleResize = () => {
      const stickyHeight = stickyRef.current?.clientHeight || window.innerHeight;

      setViewport({
        width: window.innerWidth,
        height: stickyHeight,
      });
      updateFromScroll();
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    window.addEventListener("scroll", updateFromScroll, { passive: true });

    window.setTimeout(() => jumpToScrollProgress(DEFAULT_SCROLL_PROGRESS), 120);

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("scroll", updateFromScroll);
    };
  }, [jumpToScrollProgress, updateFromScroll]);

  const avatarAnchorY = viewport.width <= 720 ? 0.58 : 0.54;
  const activeScale = camera.scale * viewZoom;
  const scrollPanY = (DEFAULT_SCROLL_PROGRESS - scrollProgress) * SCROLL_CAMERA_RANGE;
  const worldTranslateX = snapToDevicePixel(viewport.width * 0.5 - camera.x * activeScale + viewPan.x);
  const worldTranslateY = snapToDevicePixel(
    viewport.height * avatarAnchorY - camera.y * activeScale + viewPan.y + scrollPanY
  );
  const worldStyle = {
    transform: `translate3d(${worldTranslateX}px, ${worldTranslateY}px, 0) scale(${activeScale})`,
  };
  const stickyClassName = [
    "bcr-space-sticky",
    isPanning ? "bcr-space-sticky-panning" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <main className="bcr-space-demo-shell">
      <section ref={sectionRef} className="bcr-space-scroll">
        <div
          ref={stickyRef}
          className={stickyClassName}
          aria-label="Big Canada Run space progress proof of concept"
          onPointerDown={beginPan}
          onPointerMove={movePan}
          onPointerUp={endPan}
          onPointerCancel={endPan}
        >
          <SpaceWorld
            calibrationEnabled={calibrationEnabled}
            calibrationPlacementEnabled={calibrationPlacementEnabled}
            calibrationPoints={calibrationPoints}
            camera={camera}
            distanceKm={distanceKm}
            onCalibrationClick={addCalibrationPoint}
            worldStyle={worldStyle}
          />

          {calibrationEnabled && (
            <div className="bcr-calibration-panel">
              <strong>Route points</strong>
              <button
                type="button"
                className={calibrationPlacementEnabled ? "bcr-calibration-panel-active" : ""}
                onClick={() => setCalibrationPlacementEnabled((currentValue) => !currentValue)}
              >
                {calibrationPlacementEnabled ? "Move map" : "Place points"}
              </button>
              {calibrationPoints.length === 0 ? (
                <p>Use Move map to pan. Switch to Place points before clicking points 1, 2, 3, and 4.</p>
              ) : (
                <ol>
                  {calibrationPoints.map((point) => (
                    <li key={point.id}>
                      {point.id}: x {point.x}, y {point.y}
                    </li>
                  ))}
                </ol>
              )}
              {calibrationPoints.length > 0 && (
                <button type="button" onClick={() => setCalibrationPoints([])}>
                  Clear
                </button>
              )}
            </div>
          )}

          <ProgressPanel
            distanceKm={distanceKm}
            journeyPercent={journeyPercent}
            onChangeDistance={setDistanceKm}
            onChangeViewZoom={changeViewZoom}
            onPreventControlFocusScroll={preventControlFocusScroll}
            onCtaClick={trackCtaClick}
            onTogglePanel={togglePanel}
            panelCollapsed={panelCollapsed}
            progressData={progressData}
            progressDebugEnabled={progressDebugEnabled}
            stats={stats}
            viewZoom={viewZoom}
            viewZoomMax={VIEW_ZOOM_MAX}
            viewZoomMin={VIEW_ZOOM_MIN}
            viewZoomStep={VIEW_ZOOM_STEP}
          />

          <div className="bcr-scroll-hint">
            <span>Scroll</span>
          </div>
        </div>
      </section>
    </main>
  );
}
